/* eslint-disable react/jsx-no-literals */
import React, { memo, ReactNode } from 'react';

import { FormattedMessage, useIntl } from '@futrli/localisation';

import styles from './formula-driver-help.scss';

export const FormulaDriverHelp = memo(() => {
  const intl = useIntl();

  return (
    <div className={styles.formulaDriverHelp}>
      <div className={styles.introductionFormulaText}>
        <div className={styles.introductionFormulaText}>
          <FormattedMessage
            id="predict.drivers_formula_help_introduction"
            defaultMessage="Formulas help you to reference other rows of non-financial data and perform mathematical operations on that data to generate a new row. See some examples below or <link>click here</link> for extra guidance:"
            values={{
              link: (chunks: ReactNode[]) => (
                <a
                  href="https://support.futrli.com/"
                  className={styles.linkHelpCentre}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {chunks}
                </a>
              )
            }}
          />
        </div>
      </div>

      <div className={styles.functionExplain}>
        <FormattedMessage id="predict.10_percent_items" defaultMessage="10% of Items:" />
      </div>
      <div className={styles.example}>
        @{intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })} * 0.1
      </div>
      <div className={styles.functionExplain}>
        <FormattedMessage id="predict.10_percent_last_month_items" defaultMessage="10% of last month’s Items:" />
      </div>
      <div className={styles.example}>
        @{intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })}{' '}
        {intl.formatMessage({ id: 'formula.last_month', defaultMessage: 'LASTMONTH' })} * 0.1
      </div>
      <div className={styles.functionExplain}>
        <FormattedMessage
          id="predict.10_percent_last_month_items_reduces"
          defaultMessage="10% of Items, but reduces to 5% of Items should Items exceed 50,000 in a month:"
        />
      </div>
      <div className={styles.example}>
        {intl.formatMessage({ id: 'formula.if', defaultMessage: 'IF' })}(@
        {intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })} &gt; 50000, @
        {intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })} * 0.05, @
        {intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })} * 0.1)
      </div>
      <div className={styles.functionExplain}>
        <FormattedMessage
          id="predict.10_percent_last_month_items_december"
          defaultMessage="10% of last month’s items and only happens every December:"
        />
      </div>
      <div className={styles.example} style={{ marginBottom: 24 }}>
        {intl.formatMessage({ id: 'formula.if', defaultMessage: 'IF' })}(
        {intl.formatMessage({ id: 'common.month', defaultMessage: 'month' }).toUpperCase()}() = 12, @
        {intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })}{' '}
        {intl.formatMessage({ id: 'formula.last_month', defaultMessage: 'LASTMONTH' })} * 0.1, 0)
      </div>
      <div className={styles.functionExplain} style={{ marginBottom: 16 }}>
        <FormattedMessage
          id="predict.formula_further_examples"
          defaultMessage="Here are some further examples to follow to help you construct formulas of your own:"
        />
      </div>
      <div className={styles.functionExplain}>
        <FormattedMessage
          id="predict.standard_math_operator_can_use"
          defaultMessage="Standard mathematical operators can be used:"
        />
      </div>
      <div className={styles.example}>
        @{intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })} * 0.1 + @
        {intl.formatMessage({ id: 'financial_fields.other_items', defaultMessage: 'OtherItems' })}
      </div>
      <div className={styles.functionExplain}>
        <FormattedMessage
          id="predict.values_certain_date_referenced"
          defaultMessage="Values on a certain date can be referenced:"
        />
      </div>
      <div className={styles.example}>
        @{intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })}{' '}
        {intl.formatMessage({ id: 'formula.last_month', defaultMessage: 'LASTMONTH' })}
      </div>
      <div className={styles.functionExplain}>
        <FormattedMessage id="predict.for_items_in_january" defaultMessage="Or, for items in January 2020:" />
      </div>
      <div className={styles.example}>
        @{intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })}{' '}
        {intl.formatMessage({ id: 'formula.on_date', defaultMessage: 'ON DATE' })}(2020,1)
      </div>
      <div className={styles.functionExplain}>
        <FormattedMessage
          id="predict.items_2_months_ago_referenced"
          defaultMessage="Items two months ago can be referenced as"
        />
      </div>
      <div className={styles.example}>
        @{intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })}{' '}
        {intl.formatMessage({ id: 'formula.on_date', defaultMessage: 'ON DATE' })}(0, -2)
      </div>
      <div className={styles.functionExplain}>
        <FormattedMessage
          id="predict.items_1_year_2_months_ago"
          defaultMessage="Items one year and two months ago would be:"
        />
      </div>
      <div className={styles.example}>
        @{intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })}{' '}
        {intl.formatMessage({ id: 'formula.on_date', defaultMessage: 'ON DATE' })}(-1, -2)
      </div>
      <div className={styles.functionExplain}>
        <FormattedMessage
          id="predict.formulas_can_start_certain_date"
          defaultMessage="Formulas can start on a certain date (eg from 1st January 2024):"
        />
      </div>
      <div className={styles.example}>
        {intl.formatMessage({ id: 'formula.if', defaultMessage: 'IF' })}(
        {intl.formatMessage({ id: 'formula.date', defaultMessage: 'DATE' })}() &gt;={' '}
        {intl.formatMessage({ id: 'formula.date', defaultMessage: 'DATE' })}(2024, 1), @
        {intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })} * 0.1, 0)
      </div>
      <div className={styles.functionExplain}>
        <FormattedMessage
          id="predict.aggregate_function_example_rolling_average"
          defaultMessage="Aggregate functions can be used, for example to calculate the rolling average of the last 3 months:"
        />
      </div>
      <div className={styles.example} style={{ marginBottom: 0 }}>
        {intl.formatMessage({ id: 'formula.avg', defaultMessage: 'AVG' })}(@
        {intl.formatMessage({ id: 'financial_fields.items', defaultMessage: 'Items' })},{' '}
        {intl.formatMessage({ id: 'formula.date', defaultMessage: 'DATE' })}(0, -3),{' '}
        {intl.formatMessage({ id: 'formula.date', defaultMessage: 'DATE' })}(0, -1))
      </div>
    </div>
  );
});
